import { ApiRouteService } from '../services';
import { HttpClient } from './HttpClient';

/**
 * Client for WorklistAITemplate API operations
 */
export class WorklistAITemplateClient {
  constructor(private httpClient: HttpClient) {
    this.getTemplatesForWorklist = this.getTemplatesForWorklist.bind(this);
    this.getTemplate = this.getTemplate.bind(this);
    this.createTemplate = this.createTemplate.bind(this);
    this.updateTemplate = this.updateTemplate.bind(this);
    this.deleteTemplate = this.deleteTemplate.bind(this);
    this.generateSummary = this.generateSummary.bind(this);
    this.storeSummaryAsFile = this.storeSummaryAsFile.bind(this);
    this.processAutoGeneration = this.processAutoGeneration.bind(this);
    this.getTemplatesDueForAutoGeneration = this.getTemplatesDueForAutoGeneration.bind(this);
  }

  /**
   * Get all templates for a specific worklist
   */
  public async getTemplatesForWorklist(worklistViewId: number): Promise<WorklistAITemplate[]> {
    const options = await this.httpClient.createStandardOptions('GET', null);
    const url = `${ApiRouteService.getCompumedApiBaseRoute()}/api/WorklistAITemplate/worklist/${worklistViewId}`;
    return await this.httpClient.fetchAndParse<WorklistAITemplate[]>(fetch(url, options));
  }

  /**
   * Get a specific template by ID
   */
  public async getTemplate(templateId: number): Promise<WorklistAITemplate> {
    const options = await this.httpClient.createStandardOptions('GET', null);
    const url = `${ApiRouteService.getCompumedApiBaseRoute()}/api/WorklistAITemplate/${templateId}`;
    return await this.httpClient.fetchAndParse<WorklistAITemplate>(fetch(url, options));
  }

  /**
   * Create a new template
   */
  public async createTemplate(request: CreateTemplateRequest): Promise<WorklistAITemplate> {
    const options = await this.httpClient.createStandardOptions('POST', request);
    const url = `${ApiRouteService.getCompumedApiBaseRoute()}/api/WorklistAITemplate`;
    return await this.httpClient.fetchAndParse<WorklistAITemplate>(fetch(url, options));
  }

  /**
   * Update an existing template
   */
  public async updateTemplate(templateId: number, request: UpdateTemplateRequest): Promise<WorklistAITemplate> {
    const options = await this.httpClient.createStandardOptions('PUT', request);
    const url = `${ApiRouteService.getCompumedApiBaseRoute()}/api/WorklistAITemplate/${templateId}`;
    return await this.httpClient.fetchAndParse<WorklistAITemplate>(fetch(url, options));
  }

  /**
   * Delete a template
   */
  public async deleteTemplate(templateId: number): Promise<boolean> {
    const options = await this.httpClient.createStandardOptions('DELETE', null);
    const url = `${ApiRouteService.getCompumedApiBaseRoute()}/api/WorklistAITemplate/${templateId}`;
    const response = await fetch(url, options);
    return response.ok;
  }

  /**
   * Generate a summary using a template
   */
  public async generateSummary(request: GenerateSummaryRequest): Promise<string> {
    const options = await this.httpClient.createStandardOptions('POST', request);
    const url = `${ApiRouteService.getCompumedApiBaseRoute()}/api/WorklistAITemplate/${request.templateId}/generate`;
    return await this.httpClient.fetchAndParse<string>(fetch(url, options));
  }

  /**
   * Store a generated summary as a file
   */
  public async storeSummaryAsFile(request: StoreSummaryRequest): Promise<number> {
    const options = await this.httpClient.createStandardOptions('POST', request);
    const url = `${ApiRouteService.getCompumedApiBaseRoute()}/api/WorklistAITemplate/${request.templateId}/store`;
    return await this.httpClient.fetchAndParse<number>(fetch(url, options));
  }

  /**
   * Process auto-generation for all eligible templates
   */
  public async processAutoGeneration(): Promise<number> {
    const options = await this.httpClient.createStandardOptions('POST', null);
    const url = `${ApiRouteService.getCompumedApiBaseRoute()}/api/WorklistAITemplate/auto-generate`;
    return await this.httpClient.fetchAndParse<number>(fetch(url, options));
  }

  /**
   * Get templates that are due for auto-generation
   */
  public async getTemplatesDueForAutoGeneration(): Promise<WorklistAITemplate[]> {
    const options = await this.httpClient.createStandardOptions('GET', null);
    const url = `${ApiRouteService.getCompumedApiBaseRoute()}/api/WorklistAITemplate/due-for-generation`;
    return await this.httpClient.fetchAndParse<WorklistAITemplate[]>(fetch(url, options));
  }
}

/**
 * WorklistAITemplate entity
 */
export interface WorklistAITemplate {
  id: number;
  worklistViewId: number;
  name: string;
  description?: string;
  promptTemplate: string;
  liquidDataTemplate?: string;
  htmlOutputTemplate?: string;
  autoGenerate: boolean;
  minFilesForAutoGeneration: number;
  maxFileAgeHours: number;
  autoGenerationIntervalMinutes: number;
  lastGenerated?: string;
  isActive: boolean;
  aiModel?: string;
  reasoningEffort?: string;
  dateCreated: string;
  dateModified: string;
}

/**
 * Request model for creating a template
 */
export interface CreateTemplateRequest {
  worklistViewId: number;
  name: string;
  description?: string;
  promptTemplate: string;
  liquidDataTemplate?: string;
  htmlOutputTemplate?: string;
  autoGenerate: boolean;
  minFilesForAutoGeneration: number;
  maxFileAgeHours: number;
  autoGenerationIntervalMinutes: number;
  aiModel?: string;
  reasoningEffort?: string;
}

/**
 * Request model for updating a template
 */
export interface UpdateTemplateRequest {
  name?: string;
  description?: string;
  promptTemplate?: string;
  liquidDataTemplate?: string;
  htmlOutputTemplate?: string;
  autoGenerate?: boolean;
  minFilesForAutoGeneration?: number;
  maxFileAgeHours?: number;
  autoGenerationIntervalMinutes?: number;
  aiModel?: string;
  reasoningEffort?: string;
}

/**
 * Request model for generating a summary
 */
export interface GenerateSummaryRequest {
  templateId: number;
  patientId: number;
  additionalContext?: string;
  forceRegenerate?: boolean;
}

/**
 * Request model for storing a summary as a file
 */
export interface StoreSummaryRequest {
  templateId: number;
  patientId: number;
  summary: string;
  fileName?: string;
}
